import axios from 'axios';
import { portalClient } from '../portalClient';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// ==================== Types ====================

export interface PortalLoginRequest {
  cpf: string;
  birthDate: string; // YYYY-MM-DD
}

export interface PortalAuthResponse {
  patientId: string;
  name: string;
  accessToken: string;
  expiresAt: string;
  message: string;
}

export interface PortalPendingItem {
  type: 'QUESTIONNAIRE' | 'CONTENT' | 'DEMAND';
  title: string;
  description: string;
  accessToken: string;
  url: string;
  expiresAt: string | null;
  createdAt: string | null;
  estimatedMinutes: number | null;
}

export interface PortalCareLineInfo {
  id: string;
  code: string;
  name: string;
  description: string;
  currentPhase: string | null;
  currentPhaseName: string | null;
  enrolledAt: string;
}

export interface PortalDashboard {
  patientName: string;
  pendingQuestionnaires: number;
  pendingContents: number;
  openDemands: number;
  pendingItems: PortalPendingItem[];
  activeCareLines: PortalCareLineInfo[];
  lastActivityDescription?: string;
  lastActivityDate?: string;
}

export interface PortalPatientProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  birthDate: string;
  age: number;
  gender: string;
  activeProblems: string[];
  activeMedications: string[];
  allergies: string[];
  careLines: PortalCareLineInfo[];
}

export interface PortalQuestionnaire {
  id: string;
  code: string;
  name: string;
  status: string;
  accessToken: string;
  sentAt: string;
  answeredAt: string | null;
  expiresAt: string | null;
  totalScore: number | null;
  severity: string | null;
  severityLabel: string | null;
}

export interface PortalContent {
  deliveryId: string;
  contentId: string;
  title: string;
  summary: string;
  contentType: string;
  status: string;
  accessToken: string;
  sentAt: string;
  openedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  readingTimeMinutes: number | null;
}

export interface PortalDemand {
  id: string;
  type: string;
  typeLabel: string;
  title: string;
  description: string;
  status: string;
  statusLabel: string;
  urgency: string;
  createdAt: string;
  resolvedAt: string | null;
  resolution: string | null;
  resolvedByName: string | null;
}

export interface PortalCreateDemandRequest {
  type: string;
  title: string;
  description: string;
  urgency?: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

// Luna Chat Types
export interface LunaChatResponse {
  conversationId: string;
  message: string;
  suggestions?: string[];
  sentiment?: string;
  intent?: string;
  requiresHumanReview: boolean;
  alertTriggered: boolean;
  alertMessage?: string;
}

// Luna Questionnaire Help Types
export interface QuestionnaireHelpRequest {
  patientId: string;
  questionnaireCode: string;
  questionnaireName: string;
  questionCode: string;
  questionText: string;
}

export interface QuestionnaireHelpResponse {
  helpText: string;
  questionCode: string;
  cached: boolean;
}

// Security Types
export interface PortalAccessLog {
  id: string;
  eventType: string;
  eventTypeLabel: string;
  ipAddress: string;
  maskedIpAddress: string;
  deviceType: string;
  browser: string;
  success: boolean;
  failureReason: string | null;
  createdAt: string;
}

export interface PortalSecuritySettings {
  notifyPortalAccess: boolean;
  hasPhoneNumber: boolean;
  maskedPhone: string | null;
}

export interface UpdateSecuritySettingsRequest {
  notifyPortalAccess: boolean;
}

// Care Plan Types for Portal
export interface PortalCarePlanGoal {
  code: string;
  description: string;
  targetValue?: string;
  currentValue?: string;
  targetDate?: string;
  status: 'PROPOSED' | 'IN_PROGRESS' | 'ACHIEVED' | 'NOT_ACHIEVED' | 'CANCELLED';
  notes?: string;
}

export interface PortalCarePlanActivity {
  code: string;
  description: string;
  category: string;
  frequency: string;
  startDate?: string;
  endDate?: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD';
  responsibleRole: string;
  notes?: string;
}

export interface PortalCarePlan {
  id: string;
  patientId: string;
  patientName: string;
  title: string;
  description?: string;
  status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
  statusLabel: string;
  startDate?: string;
  endDate?: string;
  goals: PortalCarePlanGoal[];
  activities: PortalCarePlanActivity[];
  addressedProblems?: string[];
  notes?: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  overdue: boolean;
  goalsCount: number;
  goalsAchievedCount: number;
  activitiesCount: number;
  activitiesCompletedCount: number;
}

export interface UpdateGoalRequest {
  code: string;
  status?: 'PROPOSED' | 'IN_PROGRESS' | 'ACHIEVED' | 'NOT_ACHIEVED' | 'CANCELLED';
  currentValue?: string;
  notes?: string;
}

export interface UpdateActivityRequest {
  code: string;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD';
  notes?: string;
}

// ==================== API Functions ====================

export const portalApi = {
  // Auth
  login: async (request: PortalLoginRequest): Promise<PortalAuthResponse> => {
    const response = await axios.post<PortalAuthResponse>(
      `${API_URL}/v1/portal/auth/login`,
      request
    );
    return response.data;
  },

  logout: async (): Promise<void> => {
    await portalClient.post('/v1/portal/auth/logout');
  },

  validateSession: async (): Promise<void> => {
    await portalClient.get('/v1/portal/auth/validate');
  },

  // Dashboard
  getDashboard: async (): Promise<PortalDashboard> => {
    const response = await portalClient.get<PortalDashboard>('/v1/portal/patient/dashboard');
    return response.data;
  },

  // Profile
  getProfile: async (): Promise<PortalPatientProfile> => {
    const response = await portalClient.get<PortalPatientProfile>('/v1/portal/patient/profile');
    return response.data;
  },

  // Questionnaires
  getQuestionnaires: async (): Promise<PortalQuestionnaire[]> => {
    const response = await portalClient.get<PortalQuestionnaire[]>('/v1/portal/patient/questionnaires');
    return response.data;
  },

  getPendingQuestionnaires: async (): Promise<PortalQuestionnaire[]> => {
    const response = await portalClient.get<PortalQuestionnaire[]>('/v1/portal/patient/questionnaires/pending');
    return response.data;
  },

  // Contents
  getContents: async (): Promise<PortalContent[]> => {
    const response = await portalClient.get<PortalContent[]>('/v1/portal/patient/contents');
    return response.data;
  },

  getPendingContents: async (): Promise<PortalContent[]> => {
    const response = await portalClient.get<PortalContent[]>('/v1/portal/patient/contents/pending');
    return response.data;
  },

  // Demands
  createDemand: async (request: PortalCreateDemandRequest): Promise<PortalDemand> => {
    const response = await portalClient.post<PortalDemand>('/v1/portal/demands', request);
    return response.data;
  },

  getDemands: async (page = 0, size = 20): Promise<PaginatedResponse<PortalDemand>> => {
    const response = await portalClient.get<PaginatedResponse<PortalDemand>>('/v1/portal/demands', {
      params: { page, size },
    });
    return response.data;
  },

  getOpenDemands: async (): Promise<PortalDemand[]> => {
    const response = await portalClient.get<PortalDemand[]>('/v1/portal/demands/open');
    return response.data;
  },

  getDemandById: async (id: string): Promise<PortalDemand> => {
    const response = await portalClient.get<PortalDemand>(`/v1/portal/demands/${id}`);
    return response.data;
  },

  cancelDemand: async (id: string): Promise<void> => {
    await portalClient.delete(`/v1/portal/demands/${id}`);
  },

  // Luna Chat
  lunaChat: async (message: string, conversationId?: string): Promise<LunaChatResponse> => {
    const response = await portalClient.post<LunaChatResponse>('/v1/portal/luna/chat', {
      message,
      conversationId: conversationId || null,
    });
    return response.data;
  },

  // Luna Questionnaire Help
  lunaQuestionnaireHelp: async (
    request: QuestionnaireHelpRequest
  ): Promise<QuestionnaireHelpResponse> => {
    const response = await portalClient.post<QuestionnaireHelpResponse>(
      '/v1/portal/luna/questionnaire-help',
      request
    );
    return response.data;
  },

  // Security Settings
  getSecuritySettings: async (): Promise<PortalSecuritySettings> => {
    const response = await portalClient.get<PortalSecuritySettings>(
      '/v1/portal/patient/security/settings'
    );
    return response.data;
  },

  updateSecuritySettings: async (
    request: UpdateSecuritySettingsRequest
  ): Promise<PortalSecuritySettings> => {
    const response = await portalClient.put<PortalSecuritySettings>(
      '/v1/portal/patient/security/settings',
      request
    );
    return response.data;
  },

  // Access History
  getAccessHistory: async (
    page = 0,
    size = 20
  ): Promise<PaginatedResponse<PortalAccessLog>> => {
    const response = await portalClient.get<PaginatedResponse<PortalAccessLog>>(
      '/v1/portal/patient/security/access-history',
      { params: { page, size } }
    );
    return response.data;
  },

  // Care Plans
  getActiveCarePlans: async (): Promise<PortalCarePlan[]> => {
    const response = await portalClient.get<PortalCarePlan[]>('/v1/portal/care-plans');
    return response.data;
  },

  getAllCarePlans: async (): Promise<PortalCarePlan[]> => {
    const response = await portalClient.get<PortalCarePlan[]>('/v1/portal/care-plans/all');
    return response.data;
  },

  getCarePlanById: async (id: string): Promise<PortalCarePlan> => {
    const response = await portalClient.get<PortalCarePlan>(`/v1/portal/care-plans/${id}`);
    return response.data;
  },

  updateCarePlanGoal: async (carePlanId: string, request: UpdateGoalRequest): Promise<PortalCarePlan> => {
    const response = await portalClient.put<PortalCarePlan>(
      `/v1/portal/care-plans/${carePlanId}/goals`,
      request
    );
    return response.data;
  },

  updateCarePlanActivity: async (carePlanId: string, request: UpdateActivityRequest): Promise<PortalCarePlan> => {
    const response = await portalClient.put<PortalCarePlan>(
      `/v1/portal/care-plans/${carePlanId}/activities`,
      request
    );
    return response.data;
  },
};
